import { AppBridge } from '../App';
import { isCommonTenant } from '../Components/CommonTenant/utils/configUtils';
import { isLogEnabled, SdkFlowSelection } from './constants';
import { getEventConfig } from './jfsEventMapping';
import { addEventToQueue, getPostHogInstance } from './postHogUtils';
import {
  getChannelType,
  getIsLoanJourney,
  getIsNewUser,
  getIsNtb,
  getSdkFlowSelection,
  getTenant,
  getTrackingId,
  getUserId,
} from './storageUtils';
import { TenantType } from './types';

export const pushEventToParent = ({
  eventCode,
  errorMessage,
  errorStatus,
  meta,
  postHogEventName,
  isFromAA,
  fiType,
}: {
  eventCode: string;
  errorMessage?: string;
  errorStatus?: string | null;
  meta?: object;
  postHogEventName?: string;
  isFromAA?: boolean;
  fiType?: string;
}) => {
  pushEventToPosthog({
    eventCode: postHogEventName ? postHogEventName : eventCode,
    errorStatus,
    errorMessage,
    meta,
  });

  // If from AA and AU tenant, prefix eventCode with fiType
  const finalEventCode =
    isFromAA && getTenant() === TenantType.AU && fiType
      ? `${fiType}_${eventCode}`
      : eventCode;

  const basePayload = {
    trackingId: getTrackingId(),
    eventCode: finalEventCode,
    eventStatus: errorStatus,
    errorMessage: errorMessage,
    timeStamp: new Date(),
    meta: meta,
  };

  if (getTenant() === null) {
    if (window.opener) {
      window.opener.postMessage(JSON.stringify(basePayload), '*');
    } else {
      window.parent.postMessage(JSON.stringify(basePayload), '*');
    }
    if ((window as any).flutter_inappwebview) {
      (window as any)?.flutter_inappwebview.callHandler(
        'messageEventListener',
        JSON.stringify(basePayload)
      );
    } else if ((window as any)?.ReactNativeWebView) {
      (window as any)?.ReactNativeWebView?.postMessage(
        JSON.stringify(basePayload)
      );
    } else if ((window as any)?.SendEventToParent?.showData) {
      (window as any).SendEventToParent.showData(JSON.stringify(basePayload));
    }
  } else {
    if (
      getTenant() === TenantType.PB ||
      getTenant() === TenantType.PENSION_BAZAAR
    ) {
      window.parent.postMessage(JSON.stringify(basePayload), '*');
    } else if (getTenant() === TenantType.JFS) {
      const eventConfig = getEventConfig(eventCode);
      const appEventName = eventConfig?.appEventName || eventCode;
      AppBridge.sendEventToApp(appEventName);
    } else if (
      getTenant() === TenantType.YESBANK ||
      getTenant() === TenantType.MAHINDRA ||
      isCommonTenant()
    ) {
      if ((window as any).flutter_inappwebview) {
        (window as any)?.flutter_inappwebview.callHandler(
          'messageEventListener',
          JSON.stringify(basePayload)
        );
      } else if ((window as any)?.ReactNativeWebView) {
        (window as any)?.ReactNativeWebView?.postMessage(
          JSON.stringify(basePayload)
        );
      } else {
        if (window.opener) {
          window.opener.postMessage(JSON.stringify(basePayload), '*');
        } else {
          window.parent.postMessage(JSON.stringify(basePayload), '*');
        }
      }
    } else if (getTenant() === TenantType.AU) {
      if ((window as any)?.SendEventToParent?.showData) {
        (window as any).SendEventToParent.showData(JSON.stringify(basePayload));
      }
      window.parent.postMessage(JSON.stringify(basePayload), '*');
    } else if (getTenant() === TenantType.SIB) {
      window.parent.postMessage(
        JSON.stringify(basePayload),
        '*'
      );
    }
  }
};

export const pushEventToPosthog = ({
  eventCode,
  errorMessage,
  errorStatus,
  meta,
}: {
  eventCode: string;
  errorMessage?: string;
  errorStatus?: string | null;
  meta?: any;
}) => {
  let eventCodeLoan;
  if (getTenant() === TenantType.YESBANK) {
    if (getIsNewUser()) {
      eventCodeLoan = 'YBL_' + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = 'YBL_' + eventCode;
      } else {
        eventCodeLoan = 'YBL_' + eventCode + '_EXISTING_USER';
      }
    }
  } else if (getTenant() === TenantType.JFS) {
    if (getIsNewUser()) {
      eventCodeLoan = 'JFS_' + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = 'JFS_' + eventCode;
      } else {
        eventCodeLoan = 'JFS_' + eventCode + '_EXISTING_USER';
      }
    }
  } else if (getTenant() === TenantType.AU) {
    const auPrefix = getIsNtb() ? 'AU_NTB_' : 'AU_';
    if (getIsNewUser()) {
      eventCodeLoan = auPrefix + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = auPrefix + eventCode;
      } else {
        eventCodeLoan = auPrefix + eventCode + '_EXISTING_USER';
      }
    }
  } else if (getTenant() === TenantType.PENSION_BAZAAR) {
    if (getIsNewUser()) {
      eventCodeLoan = 'PENSION_BAZAAR_' + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = 'PENSION_BAZAAR_' + eventCode;
      } else {
        eventCodeLoan = 'PENSION_BAZAAR_' + eventCode + '_EXISTING_USER';
      }
    }
  } else if ((getTenant() === TenantType.BOB) || (getTenant() === TenantType.BOB_V2)) {
    if (getIsNewUser()) {
      eventCodeLoan = 'BOB_' + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = 'BOB_' + eventCode;
      } else {
        eventCodeLoan = 'BOB_' + eventCode + '_EXISTING_USER';
      }
    }
  } else if (getTenant() === TenantType.SIB) {
    if (getIsNewUser()) {
      eventCodeLoan = 'SIB_' + eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = 'SIB_' + eventCode;
      } else {
        eventCodeLoan = 'SIB_' + eventCode + '_EXISTING_USER';
      }
    }
  } else if (getIsLoanJourney()) {
    eventCodeLoan = 'LOAN_' + eventCode;
  } else {
    if (getIsNewUser()) {
      eventCodeLoan = eventCode;
    } else {
      if (eventCode.endsWith('_EXISTING_USER')) {
        eventCodeLoan = eventCode;
      } else {
        eventCodeLoan = eventCode + '_EXISTING_USER';
      }
    }
  }

  let finalMeta = meta;
  if (getTenant() === TenantType.PB && getSdkFlowSelection()) {
    finalMeta = {
      ...meta,
      SdkFlowSelection: SdkFlowSelection.SHOW_FIP_SELECTION,
    };
  }

  const channelType = getChannelType();
  if (channelType) {
    finalMeta = {
      ...finalMeta,
      channelType,
    };
  }
  if (isLogEnabled) {
    console.log('Posthog Event:', {
      eventCodeLoan,
      finalMeta,
    });
  }

  if (getPostHogInstance() && getPostHogInstance().isIdentificationDone) {
    getPostHogInstance().capture(eventCodeLoan, {
      trackingId: getTrackingId(),
      userId: getUserId(),
      eventCode: eventCodeLoan,
      eventStatus: errorStatus,
      errorMessage: errorMessage,
      timeStamp: new Date(),
      meta: finalMeta,
    });
  } else {
    addEventToQueue(eventCodeLoan, {
      trackingId: getTrackingId(),
      eventCode: eventCodeLoan,
      eventStatus: errorStatus,
      errorMessage: errorMessage,
      timeStamp: new Date(),
      meta: finalMeta,
    });
  }
};

export const EVENTCODES = {
  AU_EPFO_ANALYSIS_DATA_FETCHED: 'EPFO_ANALYSIS_DATA_FETCHED',
  EPF_ANALYSIS_DATA_FETCHED: 'EPF_ANALYSIS_DATA_FETCHED',
  EPF_DATA_LOADED: 'EPF_DATA_LOADED',
  CREDIT_ANALYSIS_DATA_FETCHED: 'CREDIT_ANALYSIS_DATA_FETCHED',
  CONNECT_MF_ACCOUNT: 'CONNECT_MF_ACCOUNT',
  CONNECT_STOCKS_ACCOUNT: 'CONNECT_STOCKS_ACCOUNT',
  HOME_CLICKED: 'HOME_CLICKED',
  ADD_ACCOUNT_FD_RD: 'ADD_ACCOUNT_FD_RD',
  DASHBOARD_VIEW: 'DASHBOARD_VIEW',
  DASHBOARD_EXIT: 'DASHBOARD_EXIT',
  RECEIVED_AUTO_READ_EVENT: 'RECEIVED_AUTO_READ_EVENT',
  ASSETS_FETCH_SUCCESS: 'ASSETS_FETCH_SUCCESS',
  RAW_DATA_FETCHED: 'RAW_DATA_FETCHED',
  NO_SESSION_ID: 'NO_SESSION_ID',
  QUERY_PARAMS_INFO: 'QUERY_PARAMS_INFO',
  FIP_CONFIRM_BUTTON_CLICKED: 'FIP_CONFIRM_BUTTON_CLICKED',
  FIP_SELECTED: 'FIP_SELECTED',
  FIP_SELECTED_YES_BANK: 'FIP_SELECTED_YES_BANK',
  CONNECT_BUTTON_CLICKED: 'CONNECT_BUTTON_CLICKED',
  FIP_LIST_FETCHED: 'FIP_LIST_FETCHED',
  FIP_LIST_FAILED: 'FIP_LIST_FAILED',
  FETCHING_FIP_LIST: 'FETCHING_FIP_LIST',
  SESSION_TIMEOUT: 'SESSION_TIMEOUT',
  SESSION_TIMEOUT_MDS: 'SESSION_TIMEOUT_MDS',
  SIB_KEEP_ALIVE: 'SIB_KEEP_ALIVE',
  STATUS_POLLING: 'STATUS_POLLING',
  EMAIL_MDS: 'EMAIL_MDS',
  METADATA_RECEIVED: 'METADATA_RECEIVED',
  PFM_JOURNEY: 'PFM_JOURNEY',
  LOAN_JOURNEY: 'JOURNEY',
  NEW_AUTH_PAGE_LOADED: 'NEW_AUTH_PAGE_LOADED',
  FIP_PAGE_LOADED: 'FIP_PAGE_LOADED',
  STOCKS_NEW_USER: 'STOCKS_NEW_USER',
  JOURNEY_STARTED: 'JOURNEY_STARTED',
  JOURNEY_STARTED_EXISTING_USER: 'JOURNEY_STARTED_EXISTING_USER',
  JOURNEY_STARTED_NEW_USER: 'JOURNEY_STARTED_NEW_USER',
  DASHBOARD_LOAD: 'DASHBOARD_LOAD',
  BANK_ACCOUNT_CLICKED: 'BANK_ACCOUNT_CLICKED',
  MF_ACCOUNT_CLICKED: 'MF_ACCOUNT_CLICKED',
  BACK_BUTTON_MF: 'BACK_BUTTON_MF',
  STOCKS_CLICKED: 'STOCKS_CLICKED',
  MUTUAL_FUNDS_CLICKED: 'MUTUAL_FUNDS_CLICKED',
  FIXED_DEPOSITS_CLICKED: 'FIXED_DEPOSITS_CLICKED',
  BANNER_CLICKED: 'BANNER_CLICKED',
  INSIGHTS_VIEW_ALL: 'INSIGHTS_VIEW_ALL',
  FAQ_CLICKED: 'FAQ_CLICKED',
  INSIGHT_INTEREST: 'INSIGHT_INTEREST',
  INSIGHT_BANKCHARGES: 'INSIGHT_BANKCHARGES',
  INSIGHT_DIVIDEND: 'INSIGHT_DIVIDEND',
  INSIGHT_SALARY: 'INSIGHT_SALARY',
  INSIGHT_EMI: 'INSIGHT_EMI',
  INSIGHT_TRAVEL: 'INSIGHT_TRAVEL',
  INSIGHT_SIP: 'INSIGHT_SIP',
  INSIGHT_MINBALCHARGES: 'INSIGHT_MINBALCHARGES',
  INSIGHT_CB: 'INSIGHT_CB',
  INSIGHT_REFUND: 'INSIGHT_REFUND',
  FINANCIAL_LITERACY_QUIZ_CLICKED: 'FINANCIAL_LITERACY_QUIZ_CLICKED',
  BACK_BUTTON_PFMDB: 'BACK_BUTTON_PFMDB',
  BANK_DASHBOARD: 'BANK_DASHBOARD',
  BANK_CLICKED_1: 'BANK_CLICKED_1',
  BANK_CLICKED_2: 'BANK_CLICKED_2',
  BANK_CLICKED_3: 'BANK_CLICKED_3',
  BANK_CLICKED_4: 'BANK_CLICKED_4',
  ADD_ACCOUNT_BANKS: 'ADD_ACCOUNT_BANKS',
  ADD_ACCOUNT_STOCKS: 'ADD_ACCOUNT_STOCKS',
  ADD_ACCOUNT_MF: 'ADD_ACCOUNT_MF',
  SUMMARY_INCOMING: 'SUMMARY_INCOMING',
  SUMMARY_EXPENSES: 'SUMMARY_EXPENSES',
  SUMMARY_INVESTMENT: 'SUMMARY_INVESTMENT',
  FILTER_BANK_DB: 'FILTER_BANK_DB',
  FILTER_DATE: 'FILTER_DATE',
  MONTHLY_PAYMENTS: 'MONTHLY_PAYMENTS',
  ACCOUNT_STATEMENT: 'ACCOUNT_STATEMENT',
  ACCOUNT_INSIGHTS: 'ACCOUNT_INSIGHTS',
  TRANSACTION_CAT: 'TRANSACTION_CAT',
  BANK_DETAILS: 'BANK_DETAILS',
  ACCOUNT_DETAILS: 'ACCOUNT_DETAILS',
  BANK_FILTER: 'BANK_FILTER',
  VIEW_ALL_TRANSACTIONS: 'VIEW_ALL_TRANSACTIONS',
  VIEW_ALL_RECURRING: 'VIEW_ALL_RECURRING',
  BACK_BUTTON_BANK: 'BACK_BUTTON_BANK',
  BACK_BUTTON_BANK_ACCOUNT: 'BACK_BUTTON_BANK_ACCOUNT',
  CATEGORIES: 'CATEGORIES',
  INCOMING_CAT: 'INCOMING_CAT',
  EXPENSES_CAT: 'EXPENSES_CAT',
  DATE: 'DATE',
  BACK_BUTTON_CATEGORIES: 'BACK_BUTTON_CATEGORIES',
  INSIGHTS_VIEW: 'INSIGHTS_VIEW',
  INSIGHTS_CLICKED: 'INSIGHTS_CLICKED',
  BACK_BUTTON_INSIGHTS: 'BACK_BUTTON_INSIGHTS',
  CATEGORY: 'CATEGORY',
  TRANSACTIONS: 'TRANSACTIONS',
  FILTER_TRANSACTIONS: 'FILTER_TRANSACTIONS',
  BACK_BUTTON_TRANSACTIONS: 'BACK_BUTTON_TRANSACTIONS',
  BACK_BUTTON_RECURRING: 'BACK_BUTTON_RECURRING',
  MY_MONTHLY_PAYMENT_CLICKED: 'MY_MONTHLY_PAYMENT_CLICKED',
  REVOKE_YOUR_CONSENT_CLICKED: 'REVOKE_YOUR_CONSENT_CLICKED',
  REVOKE_CONSENT_SUCCESS: 'REVOKE_CONSENT_SUCCESS',
  REVOKE_CONSENT_FAILED: 'REVOKE_CONSENT_FAILED',
  GO_BACK_CONSENT_REVOKE: 'GO_BACK_CONSENT_REVOKE',
  CONSENT_REVOKE_CLICKED: 'CONSENT_REVOKE_CLICKED',
  USER_INITIATED_REFRESH_BUTTON_CLICKED:
    'USER_INITIATED_REFRESH_BUTTON_CLICKED',
  REFRESH_FAILED: 'REFRESH_FAILED',
  REFRESH_PROCESSING: 'REFRESH_PROCESSING',
  REFRESH_COMPLETED_PARTIAL: 'REFRESH_COMPLETED_PARTIAL',
  REFRESH_COMPLETED: 'REFRESH_COMPLETED',
  AUTO_REFRESH: 'AUTO_REFRESH',
  BACK_BUTTON_PRESSED_AA: 'BACK_BUTTON_PRESSED_AA',
  BACK_BUTTON_PRESSED: 'BACK_BUTTON_PRESSED',
  HANDLE_WEBVIEW_BACK: 'HANDLE_WEBVIEW_BACK',
  NUDGE_VIEWED: 'NUDGE_VIEWED',
  NUDGE_CLICKED: 'NUDGE_CLICKED',
  NUDGE_RENDER: 'NUDGE_RENDER',
  NUDGES_LINK_ACCOUNTS_VIEWED: 'NUDGES_LINK_ACCOUNTS_VIEWED',
  NUDGES_LINK_ACCOUNTS_BANK_LINK_CLICKED:
    'NUDGES_LINK_ACCOUNTS_BANK_LINK_CLICKED',
  WELCOME_SCREEN_ASSET_MF_LINK_CLICKED: 'WELCOME_SCREEN_ASSET_MF_LINK_CLICKED',
  WELCOME_SCREEN_ASSET_BANK_LINK_CLICKED:
    'WELCOME_SCREEN_ASSET_BANK_LINK_CLICKED',
  WELCOME_SCREEN_ASSET_STOCKS_LINK_CLICKED:
    'WELCOME_SCREEN_ASSET_STOCKS_LINK_CLICKED',
  WELCOME_SCREEN_ASSET_FD_LINK_CLICKED: 'WELCOME_SCREEN_ASSET_FD_LINK_CLICKED',
  WELCOME_SCREEN_ASSET_NPS_LINK_CLICKED:
    'WELCOME_SCREEN_ASSET_NPS_LINK_CLICKED',
  WELCOME_SCREEN_VIEWED: 'WELCOME_SCREEN_VIEWED',
  ASSETS_DASHBOARD_ADD_ASSETS_CLICKED: 'ASSETS_DASHBOARD_ADD_ASSETS_CLICKED',
  TOGGLE_AMOUNTS_CLICKED: 'TOGGLE_AMOUNTS_CLICKED',
  ASSET_BANK_LINK_CLICKED: 'ASSET_BANK_LINK_CLICKED',
  ASSET_STOCKS_LINK_CLICKED: 'ASSET_STOCKS_LINK_CLICKED',
  ASSET_MUTUAL_FUND_LINK_CLICKED: 'ASSET_MUTUAL_FUND_LINK_CLICKED',
  BANK_CLICKED: 'BANK_CLICKED',
  ASSETS_DASHBOARD_VIEWED: 'ASSETS_DASHBOARD_VIEWED',
  ADD_ASSET_DRAWER_VIEWED: 'ADD_ASSET_DRAWER_VIEWED',
  ADD_ASSET_DRAWER_SAVINGS_CLICKED: 'ADD_ASSET_DRAWER_SAVINGS_CLICKED',
  ADD_ASSET_DRAWER_STOCKS_ETFS_CLICKED: 'ADD_ASSET_DRAWER_STOCKS_ETFS_CLICKED',
  ADD_ASSET_DRAWER_MUTUAL_FUNDS_CLICKED:
    'ADD_ASSET_DRAWER_MUTUAL_FUNDS_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_SAVINGS_CLICKED:
    'ADD_ASSET_DRAWER_EXISTING_SAVINGS_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_STOCKS_ETFS_CLICKED:
    'ADD_ASSET_DRAWER_EXISTING_STOCKS_ETFS_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_MUTUAL_FUNDS_CLICKED:
    'ADD_ASSET_DRAWER_EXISTING_MUTUAL_FUNDS_CLICKED',
  ADD_ASSET_DRAWER_FIXED_DEPOSITS_CLICKED:
    'ADD_ASSET_DRAWER_FIXED_DEPOSITS_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_FIXED_DEPOSITS_CLICKED:
    'ADD_ASSET_DRAWER_EXISTING_FIXED_DEPOSITS_CLICKED',
  ADD_ASSET_DRAWER_FD_CLICKED: 'ADD_ASSET_DRAWER_FD_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_FD_CLICKED: 'ADD_ASSET_DRAWER_EXISTING_FD_CLICKED',
  DASHBOARD_CASHFLOW_TAB_CLICKED_OVERVIEW:
    'DASHBOARD_CASHFLOW_TAB_CLICKED_OVERVIEW',
  DASHBOARD_CASHFLOW_TAB_CLICKED_MONEY_IN:
    'DASHBOARD_CASHFLOW_TAB_CLICKED_MONEY_IN',
  DASHBOARD_CASHFLOW_TAB_CLICKED_MONEY_OUT:
    'DASHBOARD_CASHFLOW_TAB_CLICKED_MONEY_OUT',
  DASHBOARD_CASHFLOW_MONTH_PERIOD_CLICKED:
    'DASHBOARD_CASHFLOW_MONTH_PERIOD_CLICKED',
  ACCOUNTS_L1_GRAPH_CLICKED: 'ACCOUNTS_L1_GRAPH_CLICKED',
  ACCOUNTS_L1_CHEVRON_CLICKED_BANK: 'ACCOUNTS_L1_CHEVRON_CLICKED_BANK',
  BACK_BUTTON_CLICKED: 'BACK_BUTTON_CLICKED',
  ACCOUNTS_INSIGHT_CARD_CLICKED: 'ACCOUNTS_INSIGHT_CARD_CLICKED',
  ACCOUNTS_L1_ADD_ACCOUNT_CLICKED: 'ACCOUNTS_L1_ADD_ACCOUNT_CLICKED',
  ACCOUNTS_L1_ALL_TRANSACTIONS_CLICKED: 'ACCOUNTS_L1_ALL_TRANSACTIONS_CLICKED',
  ACC_L1_CASHFLOW_TAB_CLICKED_OVERVIEW: 'ACC_L1_CASHFLOW_TAB_CLICKED_OVERVIEW',
  ACC_L1_CASHFLOW_TAB_CLICKED_MONEYIN: 'ACC_L1_CASHFLOW_TAB_CLICKED_MONEYIN',
  ACC_L1_CASHFLOW_TAB_CLICKED_MONEYOUT: 'ACC_L1_CASHFLOW_TAB_CLICKED_MONEYOUT',
  ACC_L1_CASHFLOW_MONTH_PERIOD_CLICKED: 'ACC_L1_CASHFLOW_MONTH_PERIOD_CLICKED',
  ACCOUNTS_L1_FAQ_MENU_CLICKED: 'ACCOUNTS_L1_FAQ_MENU_CLICKED',
  ACCOUNTS_L1_REMOVE_ACCOUNT_MENU_CLICKED:
    'ACCOUNTS_L1_REMOVE_ACCOUNT_MENU_CLICKED',
  BANK_REMOVE_ACCOUNT_CTA_CLICKED: 'BANK_REMOVE_ACCOUNT_CTA_CLICKED',
  BANK_KEEP_ACCOUNT_CTA_CLICKED: 'BANK_KEEP_ACCOUNT_CTA_CLICKED',
  BANK_REMOVE_ACCOUNT_FAILURE: 'BANK_REMOVE_ACCOUNT_FAILURE',
  BANK_REMOVE_ACCOUNT_SUCCESS: 'BANK_REMOVE_ACCOUNT_SUCCESS',
  BANK_REMOVE_ACCOUNT_TRY_AGAIN_CLICKED:
    'BANK_REMOVE_ACCOUNT_TRY_AGAIN_CLICKED',
  ALL_TRANSACTIONS_VIEWED: 'ALL_TRANSACTIONS_VIEWED',
  ALL_TRANSACTIONS_BACK_CLICKED: 'ALL_TRANSACTIONS_BACK_CLICKED',
  ALL_TRANSACTIONS_FILTER_MODAL_OPENED: 'ALL_TRANSACTIONS_FILTER_MODAL_OPENED',
  ALL_TRANSACTIONS_FILTER_APPLIED: 'ALL_TRANSACTIONS_FILTER_APPLIED',
  ALL_TRANSACTIONS_FILTER_CLEARED: 'ALL_TRANSACTIONS_FILTER_CLEARED',
  ACCOUNTS_L2_VIEWED: 'ACCOUNTS_L2_VIEWED',
  ACCOUNTS_L2_BACK_CLICKED: 'ACCOUNTS_L2_BACK_CLICKED',
  ACCOUNTS_L2_GRAPH_CLICKED: 'ACCOUNTS_L2_GRAPH_CLICKED',
  ACCOUNTS_L2_ALL_TRANSACTIONS_CLICKED: 'ACCOUNTS_L2_ALL_TRANSACTIONS_CLICKED',
  ACCOUNTS_L2_CATEGORY_ITEM_CLICKED: 'ACCOUNTS_L2_CATEGORY_ITEM_CLICKED',
  ACC_L2_CASHFLOW_TAB_CLICKED_OVERVIEW: 'ACC_L2_CASHFLOW_TAB_CLICKED_OVERVIEW',
  ACC_L2_CASHFLOW_TAB_CLICKED_MONEYIN: 'ACC_L2_CASHFLOW_TAB_CLICKED_MONEYIN',
  ACC_L2_CASHFLOW_TAB_CLICKED_MONEYOUT: 'ACC_L2_CASHFLOW_TAB_CLICKED_MONEYOUT',
  CASHFLOW_TAB_MONTHS_SELECTION: 'CASHFLOW_TAB_MONTHS_SELECTION',
  ACCOUNTS_L2_INSIGHT_CARD_CLICKED: 'ACCOUNTS_L2_INSIGHT_CARD_CLICKED',
  STOCKS_ETFS_VIEWED: 'STOCKS_ETFS_VIEWED',
  STOCKS_ETFS_INSIGHT_CARD_CLICKED: 'STOCKS_ETFS_INSIGHT_CARD_CLICKED',
  STOCKS_ETFS_FUND_DISTRIBUTION_INFO_CLICKED:
    'STOCKS_ETFS_FUND_DISTRIBUTION_INFO_CLICKED',
  STOCKS_ETFS_SORT_OPENED: 'STOCKS_ETFS_SORT_OPENED',
  STOCKS_ETFS_FILTER_OPENED: 'STOCKS_ETFS_FILTER_OPENED',
  STOCKS_ETFS_SORT_APPLIED: 'STOCKS_ETFS_SORT_APPLIED',
  STOCKS_ETFS_FILTER_APPLIED: 'STOCKS_ETFS_FILTER_APPLIED',
  STOCKS_ETFS_FILTER_CLEAR_ALL_CLICKED: 'STOCKS_ETFS_FILTER_CLEAR_ALL_CLICKED',
  STOCKS_ETFS_REMOVE_ACCOUNT_CLICKED: 'STOCKS_ETFS_REMOVE_ACCOUNT_CLICKED',
  STOCKS_ETFS_HOLDING_CLICKED: 'STOCKS_ETFS_HOLDING_CLICKED',
  STOCKS_ETFS_REMOVE_CTA_CLICKED: 'STOCKS_ETFS_REMOVE_CTA_CLICKED',
  STOCKS_ETFS_KEEP_CTA_CLICKED: 'STOCKS_ETFS_KEEP_CTA_CLICKED',
  STOCKS_ETFS_REMOVE_SUCCESS: 'STOCKS_ETFS_REMOVE_SUCCESS',
  STOCKS_ETFS_REMOVE_FAILURE: 'STOCKS_ETFS_REMOVE_FAILURE',
  STOCKS_ETFS_REMOVE_TRY_AGAIN_CLICKED: 'STOCKS_ETFS_REMOVE_TRY_AGAIN_CLICKED',
  ETF_TOP_HOLDINGS_VIEW_ALL_BACK_CLICKED:
    'ETF_TOP_HOLDINGS_VIEW_ALL_BACK_CLICKED',
  ETF_MARKET_CAP_DISTRIBUTION_INFO_CLICKED:
    'ETF_MARKET_CAP_DISTRIBUTION_INFO_CLICKED',
  STOCKS_FUNDAMENTALS_INFO_CLICKED: 'STOCKS_FUNDAMENTALS_INFO_CLICKED',
  TOP_HOLDINGS_VIEW_ALL_CLICKED: 'TOP_HOLDINGS_VIEW_ALL_CLICKED',
  SECTOR_ALLOCATION_VIEW_ALL_CLICKED: 'SECTOR_ALLOCATION_VIEW_ALL_CLICKED',
  ACCOUNTS_L1_VIEWED: 'ACCOUNTS_L1_VIEWED',
  STOCKS_L2_VIEWED: 'STOCKS_L2_VIEWED',
  ETF_L2_VIEWED: 'ETF_L2_VIEWED',

  // Mutual Funds events
  MFS_SORT_OPENED: 'MFS_SORT_OPENED',
  MFS_FILTER_OPENED: 'MFS_FILTER_OPENED',
  MFS_SORT_APPLIED: 'MFS_SORT_APPLIED',
  MFS_FILTER_APPLIED: 'MFS_FILTER_APPLIED',
  MFS_FILTER_CLEAR_ALL_CLICKED: 'MFS_FILTER_CLEAR_ALL_CLICKED',
  MFS_REMOVE_CTA_CLICKED: 'MFS_REMOVE_CTA_CLICKED',
  MFS_KEEP_CTA_CLICKED: 'MFS_KEEP_CTA_CLICKED',
  MFS_REMOVE_SUCCESS: 'MFS_REMOVE_SUCCESS',
  MFS_REMOVE_FAILURE: 'MFS_REMOVE_FAILURE',
  MFS_REMOVE_TRY_AGAIN_CLICKED: 'MFS_REMOVE_TRY_AGAIN_CLICKED',
  MFS_SEARCH_OPENED: 'MFS_SEARCH_OPENED',
  MFS_FUND_CARD_CLICKED: 'MFS_FUND_CARD_CLICKED',
  MFS_VIEW_ALL_HOLDINGS_CLICKED: 'MFS_VIEW_ALL_HOLDINGS_CLICKED',
  HOLDINGS_PAGE_BACK_CLICKED: 'HOLDINGS_PAGE_BACK_CLICKED',
  MFS_REMOVE_ACCOUNT_CLICKED: 'MFS_REMOVE_ACCOUNT_CLICKED',
  MF_FUND_DISTRIBUTION_INFO_CLICKED_L1: 'MF_FUND_DISTRIBUTION_INFO_CLICKED_L1',
  MF_FUND_DISTRIBUTION_INFO_CLICKED_L2: 'MF_FUND_DISTRIBUTION_INFO_CLICKED_L2',
  FAQ_VIEWED: 'FAQ_VIEWED',
  MF_HOLDING_CLICKED: 'MF_HOLDING_CLICKED',
  MFS_L1_PAGE_VIEWED: 'MFS_L1_PAGE_VIEWED',
  MFS_L2_PAGE_VIEWED: 'MFS_L2_PAGE_VIEWED',
  STKS_ETFS_IFUND_DIST_INFO_CLICKED: 'STKS_ETFS_IFUND_DIST_INFO_CLICKED',
  ERROR_BOUNDARY_TRIGGERED: 'ERROR_BOUNDARY_TRIGGERED',
  ASSET_FIXED_DEPOSITS_LINK_CLICKED: 'ASSET_FIXED_DEPOSITS_LINK_CLICKED',
  FD_SELECTED_BANK_CONTINUE_CLICKED: 'FD_SELECTED_BANK_CONTINUE_CLICKED',
  MANUAL_FD_SUBMISSION_FAILED: 'MANUAL_FD_SUBMISSION_FAILED',
  MANUAL_FD_SUBMISSION_SUCCESS: 'MANUAL_FD_SUBMISSION_SUCCESS',
  MANUAL_FD_SUBMISSION_CLICKED: 'MANUAL_FD_SUBMISSION_CLICKED',
  MANUAL_FD_DONE_CLICKED: 'MANUAL_FD_DONE_CLICKED',
  FD_ADD_MANUALLY_CLICKED: 'FD_ADD_MANUALLY_CLICKED',
  FD_L1_FAQ_MENU_CLICKED: 'FD_L1_FAQ_MENU_CLICKED',
  FD_REMOVE_ACCOUNT_MENU_CLICKED: 'FD_REMOVE_ACCOUNT_MENU_CLICKED',
  FD_REMOVE_ACCOUNT_SUCCESS: 'FD_REMOVE_ACCOUNT_SUCCESS',
  FD_REMOVE_ACCOUNT_FAILURE: 'FD_REMOVE_ACCOUNT_FAILURE',
  FD_REMOVE_ACCOUNT_CTA_CLICKED: 'FD_REMOVE_ACCOUNT_CTA_CLICKED',
  FD_KEEP_ACCOUNT_CTA_CLICKED: 'FD_KEEP_ACCOUNT_CTA_CLICKED',
  FD_REMOVE_ACCOUNT_TRY_AGAIN_CLICKED: 'FD_REMOVE_ACCOUNT_TRY_AGAIN_CLICKED',
  FD_PROMO_BANNER_BOOK_NOW_CLICKED_GOLD:
    'FD_PROMO_BANNER_BOOK_NOW_CLICKED_GOLD',
  FD_PROMO_BANNER_BOOK_NOW_CLICKED_PURPLE:
    'FD_PROMO_BANNER_BOOK_NOW_CLICKED_PURPLE',

  // AU Bank Welcome Screen events
  WELCOME_SCREEN_START_NOW_CLICKED: 'WELCOME_SCREEN_START_NOW_CLICKED',

  // AU Bank Header events
  INFO_ICON_CLICKED: 'INFO_ICON_CLICKED',
  YOUR_ASSETS_TAB_CLICKED: 'YOUR_ASSETS_TAB_CLICKED',
  CREDIT_HEALTH_TAB_CLICKED: 'CREDIT_HEALTH_TAB_CLICKED',
  INSURANCE_TAB_CLICKED: 'INSURANCE_TAB_CLICKED',
  INSURANCE_LINK_NOW_CLICKED: 'INSURANCE_LINK_NOW_CLICKED',

  // AU Insurance events
  INSURANCE_USER_INITIATED_REFRESH_BUTTON_CLICKED:
    'INSURANCE_USER_INITIATED_REFRESH_BUTTON_CLICKED',

  // AU Bank Assets Dashboard events
  BANK_LINK_NOW_CLICKED: 'BANK_LINK_NOW_CLICKED',
  STOCKS_LINK_NOW_CLICKED: 'STOCKS_LINK_NOW_CLICKED',
  MF_LINK_NOW_CLICKED: 'MF_LINK_NOW_CLICKED',
  FD_LINK_NOW_CLICKED: 'FD_LINK_NOW_CLICKED',
  FD_CLICKED: 'FD_CLICKED',
  EPF_LINK_NOW_CLICKED: 'EPF_LINK_NOW_CLICKED',
  EPF_CLICKED: 'EPF_CLICKED',
  NPS_LINK_NOW_CLICKED: 'NPS_LINK_NOW_CLICKED',
  NPS_CLICKED: 'NPS_CLICKED',
  NPS_BACK_CLICKED: 'NPS_BACK_CLICKED',
  NPS_ACCOUNT_DETAILS_CLICKED: 'NPS_ACCOUNT_DETAILS_CLICKED',
  NPS_REFRESH_CLICKED: 'NPS_REFRESH_CLICKED',
  NPS_HOLDING_CLICKED: 'NPS_HOLDING_CLICKED',
  NPS_REVOKE_YOUR_CONSENT_CLICKED: 'NPS_REVOKE_YOUR_CONSENT_CLICKED',
  NPS_REVOKE_CONSENT_SUCCESS: 'NPS_REVOKE_CONSENT_SUCCESS',
  NPS_REVOKE_CONSENT_FAILED: 'NPS_REVOKE_CONSENT_FAILED',
  NPS_UNLINK_SHEET_CLOSE_CLICKED: 'NPS_UNLINK_SHEET_CLOSE_CLICKED',
  NPS_LINKED_ACCOUNTS_BACK_CLICKED: 'NPS_LINKED_ACCOUNTS_BACK_CLICKED',
  NPS_CONSENT_REVOKE_CLICKED: 'NPS_CONSENT_REVOKE_CLICKED',
  NPS_UNLINK_CONTINUE_CLICKED: 'NPS_UNLINK_CONTINUE_CLICKED',
  NPS_TIER1_TAB_CLICKED: 'NPS_TIER1_TAB_CLICKED',
  NPS_TIER2_TAB_CLICKED: 'NPS_TIER2_TAB_CLICKED',
  ADD_ACCOUNT_NPS: 'ADD_ACCOUNT_NPS',

  // AU Bank FD/RD events
  FD_REFRESH_PROCESSING: 'FD_REFRESH_PROCESSING',
  FD_REFRESH_FAILED: 'FD_REFRESH_FAILED',
  BACK_BUTTON_FD: 'BACK_BUTTON_FD',
  FD_TOGGLE_AMOUNTS_CLICKED: 'FD_TOGGLE_AMOUNTS_CLICKED',
  FD_MANAGE_ACCOUNTS_CLICKED: 'FD_MANAGE_ACCOUNTS_CLICKED',
  FD_REVOKE_CONSENT_SUCCESS: 'FD_REVOKE_CONSENT_SUCCESS',
  FD_UNLINK_SHEET_CLOSE_CLICKED: 'FD_UNLINK_SHEET_CLOSE_CLICKED',

  // AU Bank Mutual Funds events
  MF_USER_INITIATED_REFRESH_BUTTON_CLICKED:
    'MF_USER_INITIATED_REFRESH_BUTTON_CLICKED',
  MF_REFRESH_PROCESSING: 'MF_REFRESH_PROCESSING',
  MF_REFRESH_FAILED: 'MF_REFRESH_FAILED',
  MF_TOGGLE_AMOUNTS_CLICKED: 'MF_TOGGLE_AMOUNTS_CLICKED',
  MF_ACCOUNT_DETAILS_CLICKED: 'MF_ACCOUNT_DETAILS_CLICKED',
  MF_REVOKE_YOUR_CONSENT_CLICKED: 'MF_REVOKE_YOUR_CONSENT_CLICKED',
  MF_REVOKE_CONSENT_SUCCESS: 'MF_REVOKE_CONSENT_SUCCESS',
  MF_REVOKE_CONSENT_FAILED: 'MF_REVOKE_CONSENT_FAILED',
  MF_UNLINK_SHEET_CLOSE_CLICKED: 'MF_UNLINK_SHEET_CLOSE_CLICKED',
  MF_LINKED_ACCOUNTS_BACK_CLICKED: 'MF_LINKED_ACCOUNTS_BACK_CLICKED',
  MF_CONSENT_REVOKE_CLICKED: 'MF_CONSENT_REVOKE_CLICKED',
  MF_UNLINK_CONTINUE_CLICKED: 'MF_UNLINK_CONTINUE_CLICKED',

  // AU Bank Stocks events
  BACK_BUTTON_STOCKS: 'BACK_BUTTON_STOCKS',
  STOCKS_REVOKE_CONSENT_SUCCESS: 'STOCKS_REVOKE_CONSENT_SUCCESS',
  STOCKS_UNLINK_SHEET_CLOSE_CLICKED: 'STOCKS_UNLINK_SHEET_CLOSE_CLICKED',

  BACK_BUTTON_STOCKS_HOLDING_DETAILS: 'BACK_BUTTON_STOCKS_HOLDING_DETAILS',

  // AU Bank Spends/Bank events
  BANK_USER_INITIATED_REFRESH_BUTTON_CLICKED:
    'BANK_USER_INITIATED_REFRESH_BUTTON_CLICKED',
  BANK_REFRESH_PROCESSING: 'BANK_REFRESH_PROCESSING',
  BANK_REFRESH_FAILED: 'BANK_REFRESH_FAILED',
  BANK_REVOKE_CONSENT_SUCCESS: 'BANK_REVOKE_CONSENT_SUCCESS',
  BANK_ACCOUNT_SUMMARY_MONTH_CLICKED: 'BANK_ACCOUNT_SUMMARY_MONTH_CLICKED',
  BANK_SUMMARY_INCOMING: 'BANK_SUMMARY_INCOMING',
  BANK_SUMMARY_EXPENSES: 'BANK_SUMMARY_EXPENSES',
  BANK_SUMMARY_INVESTMENT: 'BANK_SUMMARY_INVESTMENT',
  BANK_CATEGORY_MONTH_CLICKED: 'BANK_CATEGORY_MONTH_CLICKED',
  BANK_CASHFLOW_MONTH_CLICKED: 'BANK_CASHFLOW_MONTH_CLICKED',
  DURATION_FILTER_CLICKED: 'DURATION_FILTER_CLICKED',

  // AU Bank EPF events
  EPF_UAN_COPIED: 'EPF_UAN_COPIED',
  EPF_STOP_TRACKING_CLICKED: 'EPF_STOP_TRACKING_CLICKED',
  EPF_REVOKE_CONSENT_INITIATED: 'EPF_REVOKE_CONSENT_INITIATED',
  EPF_REVOKE_CONSENT_SUCCESS: 'EPF_REVOKE_CONSENT_SUCCESS',
  EPF_REVOKE_CONSENT_FAILED: 'EPF_REVOKE_CONSENT_FAILED',
  EPF_CONSENT_CONTINUE_CLICKED: 'EPF_CONSENT_CONTINUE_CLICKED',
  EPF_CONSENT_UAN_NOT_LINKED: 'EPF_CONSENT_UAN_NOT_LINKED',
  EPF_CONSENT_ERROR: 'EPF_CONSENT_ERROR',
  EPF_CONSENT_SUBMITTED: 'EPF_CONSENT_SUBMITTED',
  EPF_CONSENT_RETRY: 'EPF_CONSENT_RETRY',
  EPF_REFRESH_CLICKED: 'EPF_REFRESH_CLICKED',
  EPF_REFRESH_ERROR: 'EPF_REFRESH_ERROR',
  EPF_FETCH_ERROR: 'EPF_FETCH_ERROR',
  EPF_REFRESH_UAN_NOT_LINKED: 'EPF_REFRESH_UAN_NOT_LINKED',
  EPF_REFRESH_SUBMITTED: 'EPF_REFRESH_SUBMITTED',
  EPF_ALERT_CLICKED: 'EPF_ALERT_CLICKED',
  EPF_VIEW_ALL_EMPLOYERS: 'EPF_VIEW_ALL_EMPLOYERS',
  EPF_PROJECTED_CORPUS_CLICKED: 'EPF_PROJECTED_CORPUS_CLICKED',
  EPF_ACCOUNT_DETAILS_CLICKED: 'EPF_ACCOUNT_DETAILS_CLICKED',
  EPF_EMPLOYER_CLICKED: 'EPF_EMPLOYER_CLICKED',

  // AU Bank Credit Health events
  CIBIL_UNLOCK_BUTTON_CLICKED: 'CIBIL_UNLOCK_BUTTON_CLICKED',
  CIBIL_CONSENT_CONTINUE_CLICKED: 'CIBIL_CONSENT_CONTINUE_CLICKED',
  CIBIL_TERMS_LINK_CLICKED: 'CIBIL_TERMS_LINK_CLICKED',
  CIBIL_CONSENT_ERROR: 'CIBIL_CONSENT_ERROR',
  CIBIL_CONSENT_RETRY: 'CIBIL_CONSENT_RETRY',
  CREDIT_SCORE_UNLOCKED_CONTINUE_CLICKED:
    'CREDIT_SCORE_UNLOCKED_CONTINUE_CLICKED',
  CREDIT_FACTOR_MODAL_BACK: 'CREDIT_FACTOR_MODAL_BACK',
  CREDIT_SCORE_INFO_SHEET_BACK: 'CREDIT_SCORE_INFO_SHEET_BACK',
  CREDIT_SCORE_NOT_AVAILABLE_INFO_BACK: 'CREDIT_SCORE_NOT_AVAILABLE_INFO_BACK',
  CONSENT_ERROR_SHEET_BACK: 'CONSENT_ERROR_SHEET_BACK',
  CIBIL_TERMS_BACK: 'CIBIL_TERMS_BACK',
  CIBIL_CONSENT_BACK: 'CIBIL_CONSENT_BACK',
  LOAN_DETAILS_BACK: 'LOAN_DETAILS_BACK',
  CREDIT_CARD_DETAILS_BACK: 'CREDIT_CARD_DETAILS_BACK',
  PAYMENT_HISTORY_BACK: 'PAYMENT_HISTORY_BACK',
  CREDIT_UTILIZATION_BACK: 'CREDIT_UTILIZATION_BACK',
  CREDIT_AGE_BACK: 'CREDIT_AGE_BACK',
  CREDIT_MIX_BACK: 'CREDIT_MIX_BACK',
  NEW_ENQUIRIES_BACK: 'NEW_ENQUIRIES_BACK',
  CREDIT_HEALTH_BACK: 'CREDIT_HEALTH_BACK',
  CREDIT_SCORE_INFO_CLICKED: 'CREDIT_SCORE_INFO_CLICKED',
  CREDIT_SCORE_REFRESH_CLICKED: 'CREDIT_SCORE_REFRESH_CLICKED',
  CREDIT_SCORE_REFRESH_ERROR: 'CREDIT_SCORE_REFRESH_ERROR',
  CREDIT_SCORE_REFRESH_SUBMITTED: 'CREDIT_SCORE_REFRESH_SUBMITTED',
  PAYMENT_HISTORY_CLICKED: 'PAYMENT_HISTORY_CLICKED',
  CREDIT_UTILIZATION_CLICKED: 'CREDIT_UTILIZATION_CLICKED',
  CREDIT_AGE_CLICKED: 'CREDIT_AGE_CLICKED',
  CREDIT_MIX_CLICKED: 'CREDIT_MIX_CLICKED',
  NEW_ENQUIRIES_CLICKED: 'NEW_ENQUIRIES_CLICKED',
  ENQUIRY_ITEM_CLICKED: 'ENQUIRY_ITEM_CLICKED',

  // NPS events
  ADD_ASSET_DRAWER_NPS_CLICKED: 'ADD_ASSET_DRAWER_NPS_CLICKED',
  ADD_ASSET_DRAWER_EXISTING_NPS_CLICKED:
    'ADD_ASSET_DRAWER_EXISTING_NPS_CLICKED',
  ASSET_NPS_LINK_CLICKED: 'ASSET_NPS_LINK_CLICKED',
  ASSETS_DASHBOARD_NPS_CLICKED: 'ASSETS_DASHBOARD_NPS_CLICKED',
  NPS_L1_PAGE_VIEWED: 'NPS_L1_PAGE_VIEWED',
  NPS_L2_PAGE_VIEWED: 'NPS_L2_PAGE_VIEWED',
  NPS_TIER_TAB_CLICKED: 'NPS_TIER_TAB_CLICKED',
  NPS_TOGGLE_AMOUNTS_CLICKED: 'NPS_TOGGLE_AMOUNTS_CLICKED',
  NPS_UNDERSTANDING_SECTION_CLICKED: 'NPS_UNDERSTANDING_SECTION_CLICKED',
  NPS_REMOVE_ACCOUNT_CLICKED: 'NPS_REMOVE_ACCOUNT_CLICKED',
  NPS_REMOVE_CTA_CLICKED: 'NPS_REMOVE_CTA_CLICKED',
  NPS_KEEP_CTA_CLICKED: 'NPS_KEEP_CTA_CLICKED',
  NPS_REMOVE_SUCCESS: 'NPS_REMOVE_SUCCESS',
  NPS_REMOVE_FAILURE: 'NPS_REMOVE_FAILURE',
  NPS_REMOVE_TRY_AGAIN_CLICKED: 'NPS_REMOVE_TRY_AGAIN_CLICKED',
  NPS_FAQ_MENU_CLICKED: 'NPS_FAQ_MENU_CLICKED',
  NPS_SORT_OPENED: 'NPS_SORT_OPENED',
  NPS_SORT_APPLIED: 'NPS_SORT_APPLIED',
  NPS_FILTER_OPENED: 'NPS_FILTER_OPENED',
  NPS_FILTER_APPLIED: 'NPS_FILTER_APPLIED',
  // Yes Bank Financial Recap
  RECAP_LANDING_LOAD: 'RECAP_LANDING_LOAD',
  RECAP_LANDING_BACK_CLICKED: 'RECAP_LANDING_BACK_CLICKED',
  RECAP_TAP_TO_START_CLICKED: 'RECAP_TAP_TO_START_CLICKED',
  RECAP_STORY_BACK_CLICKED: 'RECAP_STORY_BACK_CLICKED',
  RECAP_STORY_OVERVIEW_LOAD: 'RECAP_STORY_OVERVIEW_LOAD',
  RECAP_STORY_INCOME_BREAKDOWN_LOAD: 'RECAP_STORY_INCOME_BREAKDOWN_LOAD',
  RECAP_STORY_PEAK_SPENDING_LOAD: 'RECAP_STORY_PEAK_SPENDING_LOAD',
  RECAP_STORY_TOP_CATEGORIES_LOAD: 'RECAP_STORY_TOP_CATEGORIES_LOAD',
  RECAP_STORY_SAVINGS_INVESTMENTS_LOAD: 'RECAP_STORY_SAVINGS_INVESTMENTS_LOAD',
  RECAP_STORY_SUMMARY_LOAD: 'RECAP_STORY_SUMMARY_LOAD',
  RECAP_VIEW_AGAIN_CLICKED: 'RECAP_VIEW_AGAIN_CLICKED',

  //JFS Assetlevel events
  MUTUAL_FUNDS_ASSET_LINKED: 'MUTUAL_FUNDS_ASSET_LINKED',
  STOCKS_ASSET_LINKED: 'STOCKS_ASSET_LINKED',
  SAVINGS_ASSET_LINKED: 'SAVINGS_ASSET_LINKED',
  FIXED_DEPOSIT_ASSET_LINKED: 'FIXED_DEPOSIT_ASSET_LINKED',
  NPS_ASSET_LINKED: 'NPS_ASSET_LINKED',
  ADD_ACCOUNT_DEPOSIT: 'ADD_ACCOUNT_DEPOSIT',

  // Calendar View events
  CALENDAR_VIEW_CLICKED: 'CALENDAR_VIEW_CLICKED',
  CALENDAR_DATE_SELECTED: 'CALENDAR_DATE_SELECTED',
  BACK_BUTTON_CALENDAR: 'BACK_BUTTON_CALENDAR',
};
